import { useState, useMemo, useEffect } from 'react';
import {
  Title, Group, Stack, Card, Table, Badge, TextInput, Select, Button, 
  ActionIcon, Modal, Text, Code, ScrollArea, Pagination, Box
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconTerminal2, IconSearch, IconRefresh, IconEye, IconAlertCircle, 
  IconInfoCircle, IconAlertTriangle, IconCheck
} from '@tabler/icons-react';

// Merkezi Axios yapılandırmanı içe aktarıyoruz
import api from '../api/axiosConfig';

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 10;

  const [opened, { open, close }] = useDisclosure(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Axios ile verileri çeken fonksiyon
  const fetchLogs = async () => {
    setLoading(true);
    try {
      // Base URL axiosConfig içinde tanımlı olduğu için sadece endpoint'i yazıyoruz.
      // (Eğer base URL'in sonu /api ile bitmiyorsa burayı '/api/logs' olarak kullanabilirsin)
      const response = await api.get('/logs'); 
      
      // Axios veriyi otomatik olarak parse eder, doğrudan response.data'yı alıyoruz
      setLogs(response.data);
    } catch (error) {
      console.error("Loglar çekilirken hata oluştu:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // Status Code'u Log Seviyesine Çevirme Mantığı
  const getLevelFromStatusCode = (statusCode) => {
    if (statusCode >= 500) return 'ERROR';
    if (statusCode >= 400) return 'WARNING';
    return 'INFO';
  };

  // Seviyelere göre renk ve ikon atamaları
  const getLevelConfig = (level) => {
    switch (level) {
      case 'INFO':
        return { color: 'teal', icon: <IconCheck size={14} /> };
      case 'WARNING':
        return { color: 'yellow', icon: <IconAlertTriangle size={14} /> };
      case 'ERROR':
        return { color: 'red', icon: <IconAlertCircle size={14} /> };
      default:
        return { color: 'gray', icon: <IconInfoCircle size={14} /> };
    }
  };

  const handleViewDetail = (log) => {
    setSelectedLog(log);
    open();
  };

  // Arama ve filtreleme mantığı
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const searchTarget = `${log.url} ${log.method} ${log.username || ''}`.toLowerCase();
      const matchesSearch = searchTarget.includes(search.toLowerCase());
      
      const level = getLevelFromStatusCode(log.status_code);
      const matchesLevel = levelFilter === 'ALL' || level === levelFilter;
      
      return matchesSearch && matchesLevel;
    });
  }, [logs, search, levelFilter]);

  // Sayfalama mantığı
  const paginatedLogs = useMemo(() => {
    const start = (activePage - 1) * itemsPerPage;
    return filteredLogs.slice(start, start + itemsPerPage);
  }, [filteredLogs, activePage]);

  // Tarih formatlama yardımcı fonksiyonu
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', { 
      year: 'numeric', month: '2-digit', day: '2-digit', 
      hour: '2-digit', minute: '2-digit', second: '2-digit' 
    });
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconTerminal2 size={28} color="#228be6" />
          <Title order={2}>Sistem Logları</Title>
        </Group>
        <Button 
          leftSection={<IconRefresh size={16} />} 
          variant="light" 
          onClick={fetchLogs} 
          loading={loading}
        >
          Yenile
        </Button>
      </Group>

      {/* Filtreleme Alanı */}
      <Card withBorder shadow="sm" radius="md" p="md">
        <Group align="flex-end">
          <TextInput
            label="Arama"
            placeholder="URL, metod veya kullanıcı ara..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            style={{ flex: 1 }}
          />
          <Select
            label="Durum Seviyesi"
            data={[
              { value: 'ALL', label: 'Tümü' },
              { value: 'INFO', label: 'Başarılı (2xx)' },
              { value: 'WARNING', label: 'Uyarı (4xx)' },
              { value: 'ERROR', label: 'Hata (5xx)' },
            ]}
            value={levelFilter}
            onChange={setLevelFilter}
            style={{ width: 200 }}
          />
        </Group>
      </Card>

      {/* Log Tablosu */}
      <Card withBorder shadow="sm" radius="md" p={0}>
        <ScrollArea>
          <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tarih / Saat</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th>Kullanıcı</Table.Th>
                <Table.Th>İşlem</Table.Th>
                <Table.Th>Hedef URL</Table.Th>
                <Table.Th style={{ width: 80, textAlign: 'center' }}>Detay</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedLogs.length > 0 ? (
                paginatedLogs.map((log) => {
                  const level = getLevelFromStatusCode(log.status_code);
                  const config = getLevelConfig(level);
                  
                  return (
                    <Table.Tr key={log.id}>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>
                        <Text size="sm">{formatDate(log.created_at)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge 
                          color={config.color} 
                          variant="light" 
                          leftSection={config.icon}
                        >
                          {log.status_code}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{log.username || 'Anonim'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="gray" variant="outline">{log.method}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>{log.url}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'center' }}>
                        <ActionIcon 
                          variant="light" 
                          color="blue" 
                          onClick={() => handleViewDetail(log)}
                        >
                          <IconEye size={18} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={6} style={{ textAlign: 'center', padding: '30px' }}>
                    <Text c="dimmed">Kriterlere uygun log bulunamadı.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        
        {/* Sayfalama */}
        {filteredLogs.length > 0 && (
          <Group justify="center" p="md" borderTop="1px solid #eee">
            <Pagination 
              value={activePage} 
              onChange={setActivePage} 
              total={Math.ceil(filteredLogs.length / itemsPerPage)} 
            />
          </Group>
        )}
      </Card>

      {/* Log Detay Modalı */}
      <Modal 
        opened={opened} 
        onClose={close} 
        title={<Text fw={700} size="lg">İşlem Detayı (ID: {selectedLog?.id})</Text>} 
        size="lg"
      >
        {selectedLog && (
          <Stack gap="md">
            <Group justify="space-between">
              <Badge 
                color={getLevelConfig(getLevelFromStatusCode(selectedLog.status_code)).color} 
                size="lg"
              >
                HTTP {selectedLog.status_code}
              </Badge>
              <Text size="sm" c="dimmed">{formatDate(selectedLog.created_at)}</Text>
            </Group>

            <Box>
              <Text size="sm" fw={600} c="dimmed">Kullanıcı</Text>
              <Text size="md">{selectedLog.username || 'Sistem / Anonim'}</Text>
            </Box>

            <Box>
              <Text size="sm" fw={600} c="dimmed">Gerçekleştirilen Aksiyon</Text>
              <Text size="md">
                <Code>{selectedLog.method}</Code> isteği yapıldı: {selectedLog.url}
              </Text>
            </Box>

            <Box>
              <Text size="sm" fw={600} c="dimmed" mb="xs">Teknik Detaylar</Text>
              <Code block color="gray" style={{ whiteSpace: 'pre-wrap' }}>
                {JSON.stringify({
                  client_ip: selectedLog.client_ip,
                  process_time_seconds: selectedLog.process_time,
                  method: selectedLog.method,
                  endpoint: selectedLog.url
                }, null, 2)}
              </Code>
            </Box>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

